import axios from "axios";
import { serverUrl } from "../nestserver";

/**
 * Montessori Course Board API.
 *
 * Class-keyed, unlike the senior board which hangs off a subject id. Every
 * response comes back wrapped by the backend `genRes` helper as { result: ... },
 * so each reader below unwraps `.data.result`.
 *
 * There is no global axios interceptor in this app — the token is read per call,
 * the same way every other module here does it.
 */

const base = serverUrl + "/montessori/course-board";

function authHeaders() {
  return {
    Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
  };
}

/* --------------------------------- Board --------------------------------- */

export async function fetchBoard(classId) {
  const res = await axios.get(`${base}/class/${classId}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function saveOverview(classId, overview) {
  const res = await axios.put(
    `${base}/class/${classId}/overview`,
    { overview },
    { headers: authHeaders() },
  );
  return res.data?.result;
}

/* --------------------------------- Posts --------------------------------- */

export async function fetchPosts(classId) {
  const res = await axios.get(`${base}/class/${classId}/posts`, {
    headers: authHeaders(),
  });
  return res.data?.result ?? [];
}

export async function createPost(classId, payload) {
  const res = await axios.post(`${base}/class/${classId}/posts`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function updatePost(id, payload) {
  const res = await axios.patch(`${base}/posts/${id}`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function deletePost(id) {
  const res = await axios.delete(`${base}/posts/${id}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

/* -------------------------------- Syllabus ------------------------------- */

export async function fetchSyllabus(classId) {
  const res = await axios.get(`${base}/class/${classId}/syllabus`, {
    headers: authHeaders(),
  });
  return res.data?.result ?? [];
}

export async function createSyllabus(classId, payload) {
  const res = await axios.post(`${base}/class/${classId}/syllabus`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function updateSyllabus(id, payload) {
  const res = await axios.put(`${base}/syllabus/${id}`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function deleteSyllabus(id) {
  const res = await axios.delete(`${base}/syllabus/${id}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

/**
 * Download the curriculum plan.
 *
 * The server streams the PDF, so this reads a blob and hands the browser a
 * temporary object URL rather than navigating (which would drop the auth header).
 */
export async function downloadSyllabusPdf(classId, className) {
  const response = await axios.get(`${base}/class/${classId}/syllabus/export`, {
    headers: authHeaders(),
    responseType: "blob",
  });

  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/pdf",
  });

  const fallback = `${className || "class"}-schedule.pdf`;
  const fileName =
    response.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] ||
    fallback;

  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

/* ------------------------------ Discussions ------------------------------ */

export async function fetchThreads(classId) {
  const res = await axios.get(`${base}/class/${classId}/discussion`, {
    headers: authHeaders(),
  });
  return res.data?.result ?? [];
}

export async function createThread(classId, payload) {
  const res = await axios.post(`${base}/class/${classId}/discussion`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function deleteThread(id) {
  const res = await axios.delete(`${base}/discussion/${id}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

/**
 * `classId` is the board the reply is made from. A group thread belongs to no
 * single class, so the server needs it to check the replier is attached to one.
 */
export async function addComment(discussionId, comment, classId) {
  const res = await axios.post(
    `${base}/discussion/${discussionId}/comment`,
    { comment, class_id: classId },
    { headers: authHeaders() },
  );
  return res.data?.result;
}

export async function deleteComment(id) {
  const res = await axios.delete(`${base}/comment/${id}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

/* ------------------------------------------------------------------------ */
/*                        GROUP-LEVEL COURSE BOARD                          */
/* ------------------------------------------------------------------------ */
/*
 * Authored once in the Group Console and read by every Junior branch of the
 * group. Lives under the group-console controller rather than the board
 * controller because authorization is by group membership (assertGroupActor),
 * not by class — a group admin is `role: admin` like any branch admin, so the
 * group id on the route is what distinguishes them.
 *
 * There is no branch picker and no targetSchools: a group row goes to the whole
 * group by definition.
 */

const groupBase = (groupId) => `${serverUrl}/group-console/${groupId}/course-board`;

export async function fetchGroupOverview(groupId) {
  const res = await axios.get(`${groupBase(groupId)}/overview`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function saveGroupOverview(groupId, overview) {
  const res = await axios.put(
    `${groupBase(groupId)}/overview`,
    { overview },
    { headers: authHeaders() },
  );
  return res.data?.result;
}

/**
 * The group's view-only switch. When on, branch users can view the Schedule and
 * Curriculum but not download them.
 */
export async function fetchGroupSettings(groupId) {
  const res = await axios.get(`${groupBase(groupId)}/settings`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function saveGroupSettings(groupId, viewOnly) {
  const res = await axios.put(
    `${groupBase(groupId)}/settings`,
    { view_only: viewOnly },
    { headers: authHeaders() },
  );
  return res.data?.result;
}

/**
 * The classes of one Junior branch of this group.
 *
 * `/classes` only returns the school you are currently acting in, so a group admin
 * cannot list a sibling branch's classes with it. The server checks the school is
 * a Junior branch of this group.
 */
export async function fetchGroupBranchClasses(groupId, schoolId) {
  const res = await axios.get(
    `${groupBase(groupId)}/branches/${schoolId}/classes`,
    { headers: authHeaders() },
  );
  return res.data?.result ?? [];
}

export async function fetchGroupPosts(groupId) {
  const res = await axios.get(`${groupBase(groupId)}/posts`, {
    headers: authHeaders(),
  });
  return res.data?.result ?? [];
}

export async function createGroupPost(groupId, payload) {
  const res = await axios.post(`${groupBase(groupId)}/posts`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function updateGroupPost(groupId, id, payload) {
  const res = await axios.patch(`${groupBase(groupId)}/posts/${id}`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function deleteGroupPost(groupId, id) {
  const res = await axios.delete(`${groupBase(groupId)}/posts/${id}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function fetchGroupSyllabus(groupId) {
  const res = await axios.get(`${groupBase(groupId)}/syllabus`, {
    headers: authHeaders(),
  });
  return res.data?.result ?? [];
}

export async function createGroupSyllabus(groupId, payload) {
  const res = await axios.post(`${groupBase(groupId)}/syllabus`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function updateGroupSyllabus(groupId, id, payload) {
  const res = await axios.put(`${groupBase(groupId)}/syllabus/${id}`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function deleteGroupSyllabus(groupId, id) {
  const res = await axios.delete(`${groupBase(groupId)}/syllabus/${id}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function fetchGroupThreads(groupId) {
  const res = await axios.get(`${groupBase(groupId)}/discussion`, {
    headers: authHeaders(),
  });
  return res.data?.result ?? [];
}

export async function createGroupThread(groupId, payload) {
  const res = await axios.post(`${groupBase(groupId)}/discussion`, payload, {
    headers: authHeaders(),
  });
  return res.data?.result;
}

export async function deleteGroupThread(groupId, id) {
  const res = await axios.delete(`${groupBase(groupId)}/discussion/${id}`, {
    headers: authHeaders(),
  });
  return res.data?.result;
}
